import React, { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import {
  ChevronDown,
  ChevronRight,
  FileText,
  GitBranch,
  Plus,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  HiringProcessAssigneeRole,
  HiringProcessItemType,
} from "@api/shared-runtime/enums";
import {
  LocalFileInput,
  LocalFile,
} from "@/components/file-upload/local-file-input";
import {
  RICH_TEXT_ITEM_TYPES,
  TEMPLATE_CONTENT_ITEM_TYPES,
  getAssigneeBadgeClass,
  getItemTypeBadgeClass,
  getItemTypeBadgeText,
} from "./constants";
import { PredecessorPicker } from "./predecessor-picker";
import { CredentialFileTypeSelect } from "./credential-file-type-select";
import type {
  ExistingAttachmentsMap,
  HiringProcessTemplateFormValues,
} from "./schema";

interface ItemRowProps {
  stageIndex: number;
  itemIndex: number;
  totalItems: number;
  form: ReturnType<typeof useForm<HiringProcessTemplateFormValues>>;
  availableForms: { id: string; name: string }[];
  onRemove: (index: number) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onCreateForm: (stageIndex: number, itemIndex: number) => void;
  onEditFormSpec: (stageIndex: number, itemIndex: number) => void;
  localFilesRef: React.MutableRefObject<Map<string, LocalFile[]>>;
  existingAttachments?: ExistingAttachmentsMap;
  enableDocumentSigning: boolean;
  hasDocShareError: boolean;
}

export function ItemRow({
  stageIndex,
  itemIndex,
  totalItems,
  form,
  availableForms,
  onRemove,
  onMoveUp,
  onMoveDown,
  onCreateForm,
  onEditFormSpec,
  localFilesRef,
  existingAttachments,
  enableDocumentSigning,
  hasDocShareError,
}: ItemRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const itemErrors =
    form.formState.errors.stages?.[stageIndex]?.items?.[itemIndex];

  useEffect(() => {
    if (itemErrors || hasDocShareError) {
      setIsExpanded(true);
    }
  }, [itemErrors, hasDocShareError]);

  const itemKey = `${stageIndex}-${itemIndex}`;
  const [localFiles, setLocalFiles] = useState<LocalFile[]>(
    () => localFilesRef.current.get(itemKey) ?? [],
  );

  const handleLocalFilesChange = (files: LocalFile[]) => {
    setLocalFiles(files);
    localFilesRef.current.set(itemKey, files);
  };

  const base = `stages.${stageIndex}.items.${itemIndex}` as const;
  const keptFileUploadIds = new Set(form.watch(`${base}.fileUploadIds`) ?? []);
  const existingFiles = (existingAttachments?.get(itemKey) ?? []).filter((f) =>
    keptFileUploadIds.has(f.fileUploadId),
  );

  const {
    fields: linkFields,
    append: appendLink,
    remove: removeLink,
  } = useFieldArray({
    control: form.control,
    name: `stages.${stageIndex}.items.${itemIndex}.links`,
  });

  const handleAddLink = () => {
    appendLink({ url: "", label: null, sortOrder: linkFields.length });
  };

  const handleRemoveExistingFile = (fileUploadId: string) => {
    form.setValue(
      `${base}.fileUploadIds`,
      (form.getValues(`${base}.fileUploadIds`) ?? []).filter(
        (id) => id !== fileUploadId,
      ),
      { shouldDirty: true },
    );
  };

  const itemType = form.watch(
    `stages.${stageIndex}.items.${itemIndex}.itemType`,
  );
  const assigneeRole = form.watch(
    `stages.${stageIndex}.items.${itemIndex}.assigneeRole`,
  );
  const isRequired = form.watch(
    `stages.${stageIndex}.items.${itemIndex}.isRequired`,
  );
  const title = form.watch(`stages.${stageIndex}.items.${itemIndex}.title`);
  const predecessors =
    form.watch(`stages.${stageIndex}.items.${itemIndex}.predecessors`) ?? [];

  const newFormSpec = form.watch(
    `stages.${stageIndex}.items.${itemIndex}.newFormSpec`,
  );
  const [sharedResourceKind, setSharedResourceKind] = useState<"file" | "link">(
    () => {
      const links = form.getValues(`${base}.links`) ?? [];
      const hasFiles = existingFiles.length > 0 || localFiles.length > 0;
      return links.length > 0 && !hasFiles ? "link" : "file";
    },
  );

  const isFormItem = itemType === HiringProcessItemType.Form;
  const needsRichText = RICH_TEXT_ITEM_TYPES.includes(
    itemType as HiringProcessItemType,
  );
  const hasTemplateContent = TEMPLATE_CONTENT_ITEM_TYPES.includes(
    itemType as HiringProcessItemType,
  );
  const isSharedResource = itemType === HiringProcessItemType.DocumentShare;
  const isDocumentUpload = itemType === HiringProcessItemType.DocumentUpload;
  const isPdfForm = itemType === HiringProcessItemType.DocumentSigning;

  // The type picker is shown as user-facing categories instead of exposing the
  // implementation enum names.
  const typeSelected = !!itemType;
  const category:
    | ""
    | "task"
    | "form"
    | "shared_file"
    | "shared_link"
    | "upload"
    | "pdf" = !itemType
    ? ""
    : itemType === HiringProcessItemType.Task
      ? "task"
      : itemType === HiringProcessItemType.Form
        ? "form"
        : itemType === HiringProcessItemType.DocumentShare
          ? sharedResourceKind === "link"
            ? "shared_link"
            : "shared_file"
          : itemType === HiringProcessItemType.DocumentSigning
            ? "pdf"
            : "upload";
  const showAssigneeField =
    typeSelected && !isSharedResource && !isPdfForm && !isDocumentUpload;
  const assigneeBadgeText =
    assigneeRole === HiringProcessAssigneeRole.Provider ? "Provider" : "Org";

  // Set the underlying itemType and clear slots that don't apply to it.
  const applyItemType = (value: HiringProcessItemType) => {
    form.setValue(`${base}.itemType`, value, { shouldDirty: true });
    if (value !== HiringProcessItemType.Form) {
      form.setValue(`${base}.formId`, null);
    }
    if (value === HiringProcessItemType.DocumentShare) {
      form.setValue(`${base}.assigneeRole`, HiringProcessAssigneeRole.Provider);
    }
    if (value === HiringProcessItemType.DocumentSigning) {
      form.setValue(`${base}.assigneeRole`, HiringProcessAssigneeRole.Provider);
    }
    if (value === HiringProcessItemType.DocumentUpload) {
      form.setValue(`${base}.assigneeRole`, HiringProcessAssigneeRole.Provider);
    }
    if (!RICH_TEXT_ITEM_TYPES.includes(value)) {
      form.setValue(`${base}.richTextContent`, null);
    }
    // Per-candidate upload carries no template-level files/links.
    if (value === HiringProcessItemType.DocumentUpload) {
      form.setValue(`${base}.fileUploadIds`, []);
      form.setValue(`${base}.links`, []);
    }
  };

  const applySharedResourceType = (kind: "file" | "link") => {
    setSharedResourceKind(kind);
    applyItemType(HiringProcessItemType.DocumentShare);
    if (kind === "file") {
      form.setValue(`${base}.links`, [], { shouldDirty: true });
    } else {
      setLocalFiles([]);
      localFilesRef.current.set(itemKey, []);
      form.setValue(`${base}.fileUploadIds`, [], { shouldDirty: true });
      if (linkFields.length === 0) {
        appendLink({ url: "", label: null, sortOrder: 0 });
      }
    }
  };

  useEffect(() => {
    if (
      itemType === HiringProcessItemType.DocumentShare &&
      assigneeRole !== HiringProcessAssigneeRole.Provider
    ) {
      form.setValue(
        `${base}.assigneeRole`,
        HiringProcessAssigneeRole.Provider,
        {
          shouldDirty: true,
        },
      );
    }
    if (
      (itemType === HiringProcessItemType.DocumentSigning ||
        itemType === HiringProcessItemType.DocumentUpload) &&
      assigneeRole !== HiringProcessAssigneeRole.Provider
    ) {
      form.setValue(
        `${base}.assigneeRole`,
        HiringProcessAssigneeRole.Provider,
        {
          shouldDirty: true,
        },
      );
    }
  }, [assigneeRole, base, form, itemType]);

  const typeDescription = !itemType
    ? ""
    : itemType === HiringProcessItemType.Task
      ? "Asks the person to check a box when they believe the item is done. It doesn't force them to take any action."
      : itemType === HiringProcessItemType.Form
        ? "Asks the person to fill out a series of questions — e.g. sent to the provider for details about their work experience, or used by the organization for interview feedback."
        : itemType === HiringProcessItemType.DocumentShare
          ? sharedResourceKind === "link"
            ? "A link added once and shown to every candidate."
            : "A file added once and shown to every candidate."
          : itemType === HiringProcessItemType.DocumentUpload
            ? "A file the provider submits for this candidate."
            : "Upload a PDF separately for each candidate, then the candidate completes it. If every candidate should complete the same PDF, use Form instead.";

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      {/* Compact summary row */}
      <div className="flex items-center gap-1.5 sm:gap-2 rounded-md border border-gray-100 bg-gray-50 px-1.5 sm:px-2 py-1.5 group hover:border-gray-200 transition-colors">
        {/* Item number */}
        <span className="shrink-0 text-xs font-mono text-gray-400 w-4 text-center">
          {itemIndex + 1}
        </span>

        {/* Title — click to expand */}
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex-1 min-w-0 text-left flex items-center gap-1.5"
            aria-label={isExpanded ? "Collapse item" : "Expand item to edit"}
          >
            {isExpanded ? (
              <ChevronDown
                size={12}
                className="shrink-0 text-gray-400 transition-transform"
              />
            ) : (
              <ChevronRight
                size={12}
                className="shrink-0 text-gray-400 transition-transform"
              />
            )}
            <span
              className={`text-sm truncate ${title ? "text-gray-800" : "text-gray-400 italic"}`}
            >
              {title || `Item ${itemIndex + 1} — click to edit`}
            </span>
          </button>
        </CollapsibleTrigger>

        {/* Compact badges */}
        <div className="flex items-center gap-1 shrink-0">
          {typeSelected && itemType !== HiringProcessItemType.Task && (
            <Badge
              variant="outline"
              className={`text-xs h-5 px-1.5 ${getItemTypeBadgeClass(itemType as HiringProcessItemType)}`}
            >
              {getItemTypeBadgeText(itemType as HiringProcessItemType)}
            </Badge>
          )}
          {typeSelected &&
            !isSharedResource &&
            !isPdfForm &&
            !isDocumentUpload && (
              <Badge
                variant="outline"
                className={`text-xs h-5 px-1.5 ${getAssigneeBadgeClass(assigneeRole as HiringProcessAssigneeRole)}`}
              >
                {assigneeBadgeText}
              </Badge>
            )}
          {predecessors.length > 0 && (
            <Badge
              variant="outline"
              className="text-xs h-5 px-1.5 gap-1 bg-gray-100 text-gray-600 border-gray-200"
            >
              <GitBranch size={10} className="shrink-0" />
              Depends on {predecessors.length}
            </Badge>
          )}
          {!isRequired && (
            <span className="text-xs text-gray-400 italic">opt</span>
          )}
        </div>

        {/* Reorder controls */}
        <div className="flex gap-0.5 shrink-0 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={() => onMoveUp(itemIndex)}
            disabled={itemIndex === 0}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-25 disabled:cursor-not-allowed p-1.5 rounded hover:bg-gray-200 transition-colors"
            aria-label="Move item up"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="2,8 6,4 10,8" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => onMoveDown(itemIndex)}
            disabled={itemIndex === totalItems - 1}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-25 disabled:cursor-not-allowed p-1.5 rounded hover:bg-gray-200 transition-colors"
            aria-label="Move item down"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="2,4 6,8 10,4" />
            </svg>
          </button>
        </div>

        {/* Remove */}
        <button
          type="button"
          onClick={() => onRemove(itemIndex)}
          className="text-red-300 hover:text-red-500 shrink-0 p-1.5 rounded hover:bg-red-50 transition-colors sm:opacity-0 sm:group-hover:opacity-100"
          aria-label="Remove item"
        >
          <Trash2 size={16} />
        </button>
      </div>

      {/* Expanded detail panel */}
      <CollapsibleContent>
        <div className="border border-t-0 border-gray-100 rounded-b-md bg-white px-3 py-3 space-y-3">
          {/* Title field */}
          <FormField
            control={form.control}
            name={`stages.${stageIndex}.items.${itemIndex}.title`}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs text-gray-600">Title</FormLabel>
                <FormControl>
                  <Input
                    placeholder={`Item ${itemIndex + 1} title`}
                    className="h-8 py-1 text-sm"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Description */}
          <FormField
            control={form.control}
            name={`stages.${stageIndex}.items.${itemIndex}.description`}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs text-gray-600">
                  Description (optional)
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="Description for this item..."
                    className="h-8 py-1 text-sm"
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Type first; assignee + required only appear once a type is chosen */}
          <div className="flex flex-wrap gap-3">
            <FormField
              control={form.control}
              name={`stages.${stageIndex}.items.${itemIndex}.itemType`}
              render={() => (
                <FormItem className="flex-1 min-w-[160px]">
                  <FormLabel className="text-xs text-gray-600">
                    Item Type
                  </FormLabel>
                  <Select
                    value={category}
                    onValueChange={(cat) => {
                      if (cat === "task") {
                        applyItemType(HiringProcessItemType.Task);
                      } else if (cat === "form") {
                        applyItemType(HiringProcessItemType.Form);
                      } else if (cat === "shared_file") {
                        applySharedResourceType("file");
                      } else if (cat === "shared_link") {
                        applySharedResourceType("link");
                      } else if (cat === "upload") {
                        applyItemType(HiringProcessItemType.DocumentUpload);
                      } else if (cat === "pdf" && enableDocumentSigning) {
                        applyItemType(HiringProcessItemType.DocumentSigning);
                      }
                    }}
                  >
                    <FormControl>
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue placeholder="Select a type..." />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="task">To-do</SelectItem>
                      <SelectItem value="form">Form</SelectItem>
                      <SelectItem value="shared_file">Shared file</SelectItem>
                      <SelectItem value="shared_link">Shared link</SelectItem>
                      <SelectItem value="upload">File request</SelectItem>
                      {enableDocumentSigning && (
                        <SelectItem value="pdf">PDF to complete</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {showAssigneeField && (
              <FormField
                control={form.control}
                name={`stages.${stageIndex}.items.${itemIndex}.assigneeRole`}
                render={({ field }) => (
                  <FormItem className="flex-1 min-w-[130px]">
                    <FormLabel className="text-xs text-gray-600">
                      Assigned to
                    </FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="h-8 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={HiringProcessAssigneeRole.Provider}>
                          Provider
                        </SelectItem>
                        <SelectItem
                          value={HiringProcessAssigneeRole.Organization}
                        >
                          Organization
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {typeSelected && (
              <FormField
                control={form.control}
                name={`stages.${stageIndex}.items.${itemIndex}.isRequired`}
                render={({ field }) => (
                  <FormItem className="flex items-end gap-2 pb-0.5">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        className="scale-90"
                      />
                    </FormControl>
                    <FormLabel className="text-xs text-gray-600 mb-0">
                      Required
                    </FormLabel>
                  </FormItem>
                )}
              />
            )}
          </div>

          {/* Plain-language description of the chosen type. */}
          {typeSelected && (
            <p className="text-xs text-gray-500 -mt-1">{typeDescription}</p>
          )}

          {isPdfForm && (
            <div className="flex flex-wrap gap-2 text-xs text-gray-600">
              <Badge
                variant="outline"
                className="bg-green-50 text-green-700 border-green-200"
              >
                Prepared by: Organization
              </Badge>
              <Badge
                variant="outline"
                className="bg-purple-50 text-purple-700 border-purple-200"
              >
                Completed by: Candidate
              </Badge>
            </div>
          )}

          {isDocumentUpload && (
            <div className="flex flex-wrap gap-2 text-xs text-gray-600">
              <Badge
                variant="outline"
                className="bg-purple-50 text-purple-700 border-purple-200"
              >
                Provided by: Provider
              </Badge>
            </div>
          )}

          {/* Form selector — only when type is Form */}
          {isFormItem && !newFormSpec && (
            <FormField
              control={form.control}
              name={`stages.${stageIndex}.items.${itemIndex}.formId`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs text-gray-600">
                    Linked Form
                  </FormLabel>
                  <div className="flex gap-2">
                    <Select
                      value={field.value ?? ""}
                      onValueChange={(value) => field.onChange(value || null)}
                    >
                      <FormControl>
                        <SelectTrigger className="h-8 text-sm">
                          <SelectValue placeholder="Select a form..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {!availableForms || availableForms.length === 0 ? (
                          <div className="px-2 py-2 text-xs text-gray-500">
                            No forms available.
                          </div>
                        ) : (
                          availableForms.map((f) => (
                            <SelectItem key={f.id} value={f.id}>
                              {f.name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <button
                      type="button"
                      onClick={() => onCreateForm(stageIndex, itemIndex)}
                      className="shrink-0 inline-flex items-center gap-1 rounded-md border border-dashed border-blue-300 px-2 h-8 text-xs text-blue-600 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                    >
                      <Plus size={12} />
                      New
                    </button>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {/* AI-proposed form spec preview */}
          {isFormItem && newFormSpec && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <FormLabel className="text-xs text-gray-600">
                  New Form (will be created on save)
                </FormLabel>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onEditFormSpec(stageIndex, itemIndex)}
                    className="text-xs text-blue-600 hover:text-blue-700"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      form.setValue(
                        `stages.${stageIndex}.items.${itemIndex}.newFormSpec`,
                        null,
                      );
                    }}
                    className="text-xs text-red-500 hover:text-red-600"
                  >
                    Remove
                  </button>
                </div>
              </div>
              <button
                type="button"
                onClick={() => onEditFormSpec(stageIndex, itemIndex)}
                className="w-full text-left rounded-md border border-blue-200 bg-blue-50 px-3 py-2 space-y-1.5 hover:border-blue-300 hover:bg-blue-100 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-blue-900">
                    {newFormSpec.name}
                  </span>
                  <Badge
                    variant="outline"
                    className="text-xs h-4 px-1.5 bg-blue-100 text-blue-700 border-blue-300"
                  >
                    {newFormSpec.fields?.length ?? 0} field
                    {(newFormSpec.fields?.length ?? 0) !== 1 ? "s" : ""}
                  </Badge>
                </div>
                {newFormSpec.description && (
                  <p className="text-xs text-blue-700">
                    {newFormSpec.description}
                  </p>
                )}
                <div className="space-y-0.5">
                  {newFormSpec.fields?.map(
                    (
                      f: {
                        label: string;
                        fieldType: string;
                        isRequired: boolean;
                      },
                      i: number,
                    ) => (
                      <div
                        key={i}
                        className="text-xs text-blue-800 flex items-center gap-1.5"
                      >
                        <span className="text-blue-400 font-mono w-4 text-right">
                          {i + 1}.
                        </span>
                        <span>{f.label}</span>
                        <span className="text-blue-500">({f.fieldType})</span>
                        {!f.isRequired && (
                          <span className="text-blue-400 italic">optional</span>
                        )}
                      </div>
                    ),
                  )}
                </div>
              </button>
            </div>
          )}

          {/* Instructions — for DocumentShare and DocumentUpload */}
          {needsRichText && (
            <FormField
              control={form.control}
              name={`stages.${stageIndex}.items.${itemIndex}.richTextContent`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs text-gray-600">
                    {isDocumentUpload
                      ? "Instructions for your team (optional)"
                      : "Instructions (optional)"}
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={
                        isDocumentUpload
                          ? "e.g. Upload the signed offer letter, or paste a link to the DocuSign envelope..."
                          : "Add instructions or notes for the provider..."
                      }
                      rows={3}
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {isDocumentUpload &&
            assigneeRole === HiringProcessAssigneeRole.Provider && (
              <FormField
                control={form.control}
                name={`stages.${stageIndex}.items.${itemIndex}.requestedCredentialFileTypeId`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs text-gray-600">
                      Credential type hint{" "}
                      <span className="text-gray-400">(optional)</span>
                    </FormLabel>
                    <div className="rounded-md bg-blue-50 border border-blue-100 px-3 py-2 space-y-2">
                      <p className="text-xs text-blue-800">
                        When set, providers will be offered the option to attach
                        an existing credential from their library and uploads
                        will default to saving back to their library.
                      </p>
                      <FormControl>
                        <CredentialFileTypeSelect
                          value={field.value ?? null}
                          onChange={(val) => field.onChange(val)}
                        />
                      </FormControl>
                      <p className="text-xs text-gray-500">
                        Leave blank for application-only uploads (no library
                        link).
                      </p>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

          {isDocumentUpload &&
            assigneeRole !== HiringProcessAssigneeRole.Provider && (
              <div className="rounded-md bg-blue-50 border border-blue-100 px-3 py-2 text-xs text-blue-800">
                Your team adds a file or link per candidate at execution time —
                no template content is needed here.
              </div>
            )}

          {/* Template-level content — DocumentShare only */}
          {hasTemplateContent && (
            <>
              {sharedResourceKind === "file" && (
                <div className="space-y-2">
                  <Label className="text-xs text-gray-600">Shared File</Label>

                  {/* Already-uploaded files (read-only) */}
                  {existingFiles.length > 0 && (
                    <div className="space-y-1.5">
                      {existingFiles.map((ef) => (
                        <div
                          key={ef.fileUploadId}
                          className="flex items-center gap-2 px-2.5 py-1.5 bg-gray-50 rounded-md border border-gray-100 text-sm"
                        >
                          <FileText
                            size={14}
                            className="text-gray-400 shrink-0"
                          />
                          <span className="flex-1 min-w-0 truncate text-gray-700">
                            {ef.fileName}
                          </span>
                          <span
                            className={`text-xs shrink-0 ${
                              ef.scanStatus === "quarantined"
                                ? "text-red-600"
                                : ef.scanStatus === "clean"
                                  ? "text-green-600"
                                  : "text-amber-600"
                            }`}
                          >
                            {ef.scanStatus === "quarantined"
                              ? "Removed by security scan"
                              : ef.scanStatus === "clean"
                                ? "Ready"
                                : "Scanning"}
                          </span>
                          <button
                            type="button"
                            className="shrink-0 rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                            aria-label={`Remove ${ef.fileName}`}
                            onClick={() =>
                              handleRemoveExistingFile(ef.fileUploadId)
                            }
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {existingFiles.some(
                    (ef) => ef.scanStatus === "quarantined",
                  ) && (
                    <p className="text-sm text-red-600">
                      Remove the rejected file before saving this shared
                      document step.
                    </p>
                  )}

                  {/* New files to upload on save */}
                  <LocalFileInput
                    files={localFiles}
                    onFilesChange={handleLocalFilesChange}
                    compact={existingFiles.length > 0}
                  />

                  {hasDocShareError && (
                    <p className="text-sm text-red-600">
                      At least one shared file is required.
                    </p>
                  )}
                </div>
              )}

              {sharedResourceKind === "link" && (
                <div className="space-y-2">
                  <Label className="text-xs text-gray-600">Shared Link</Label>
                  {linkFields.length > 0 && (
                    <div className="space-y-1.5">
                      {linkFields.map((linkField, linkIdx) => (
                        <div
                          key={linkField.id}
                          className="flex items-start gap-1.5"
                        >
                          <div className="flex-1 grid grid-cols-[2fr_3fr] gap-1.5">
                            <FormField
                              control={form.control}
                              name={`stages.${stageIndex}.items.${itemIndex}.links.${linkIdx}.label`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <Input
                                      placeholder="Label (optional)"
                                      className="h-8 py-1 text-sm"
                                      {...field}
                                      value={field.value ?? ""}
                                      onChange={(e) =>
                                        field.onChange(e.target.value || null)
                                      }
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name={`stages.${stageIndex}.items.${itemIndex}.links.${linkIdx}.url`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <Input
                                      type="url"
                                      placeholder="https://example.com"
                                      className="h-8 py-1 text-sm"
                                      {...field}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-gray-400 hover:text-red-600"
                            aria-label="Remove link"
                            onClick={() => removeLink(linkIdx)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  {hasDocShareError && (
                    <p className="text-sm text-red-600">
                      At least one shared link is required.
                    </p>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs text-gray-600 border-dashed"
                    onClick={handleAddLink}
                  >
                    <Plus size={12} className="mr-1" />
                    Add Link
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Predecessor picker */}
          <PredecessorPicker
            stageIndex={stageIndex}
            itemIndex={itemIndex}
            form={form}
          />

          {/* Delete button inside expanded view */}
          <div className="pt-2 border-t border-gray-100 mt-3">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-red-500 hover:text-red-600 hover:bg-red-50 h-8 px-3 text-xs"
              onClick={() => onRemove(itemIndex)}
            >
              <Trash2 size={14} className="mr-1.5" />
              Delete Item
            </Button>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
